import { useState, useEffect, useCallback, useMemo } from 'react';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
const token = () => localStorage.getItem('token');
const fmt = (n) =>
  `GHS ${Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtShort = (n) => {
  const v = Number(n || 0);
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toFixed(0);
};
const pct = (n) => `${Number(n || 0).toFixed(1)}%`;

const PERIODS = ['7d', '30d', '90d'];
const PERIOD_LABELS = { '7d': '7D', '30d': '30D', '90d': '90D' };
const COLORS = ['#F0B429', '#00C9B1', '#A78BFA', '#38BDF8', '#FF6B6B', '#FB7185', '#FFCD3C'];

// ── Styles ────────────────────────────────────────────────────────────────────
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Cabinet+Grotesk:wght@400;500;700;800;900&family=Instrument+Serif:ital@0;1&family=JetBrains+Mono:wght@400;600&display=swap');
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
@keyframes an-blink { 0%,100%{opacity:1} 50%{opacity:0.3} }
.an-wrap { font-family: 'Cabinet Grotesk', sans-serif; }
.an-card {
  background: #1E2235;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 18px;
  padding: 22px;
  transition: border-color 0.2s;
}
.an-card:hover { border-color: rgba(255,255,255,0.1); }
.an-kpi {
  background: #1E2235;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 16px;
  padding: 18px;
  position: relative;
  overflow: hidden;
  transition: transform 0.2s, border-color 0.2s;
  cursor: default;
}
.an-kpi:hover { transform: translateY(-2px); border-color: rgba(255,255,255,0.1); }
.an-chip { font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 100px; }
.an-chip.gold { background: rgba(240,180,41,0.12); color: #F0B429; }
.an-chip.teal { background: rgba(0,201,177,0.12); color: #00C9B1; }
.an-chip.coral { background: rgba(255,107,107,0.12); color: #FF6B6B; }
.an-chip.lav { background: rgba(167,139,250,0.12); color: #A78BFA; }
.an-sw {
  font-size: 11px; font-weight: 700; padding: 5px 10px; border-radius: 6px;
  cursor: pointer; color: #6B7A99; transition: all 0.15s;
  border: none; background: none; font-family: 'Cabinet Grotesk', sans-serif;
}
.an-sw.on { background: #1E2235; color: #FFFFFF; box-shadow: 0 1px 4px rgba(0,0,0,0.3); }
.an-sw:hover:not(.on) { color: #FFFFFF; }
.an-prod-row {
  display: flex; align-items: center; gap: 12px; padding: 10px;
  border-radius: 10px; cursor: pointer; transition: background 0.12s; margin-bottom: 2px;
}
.an-prod-row:hover { background: rgba(255,255,255,0.04); }
.an-insight {
  display: flex; align-items: flex-start; gap: 12px; padding: 14px;
  border-radius: 12px; border: 1px solid;
}
.an-insight.gold { background: rgba(240,180,41,0.05); border-color: rgba(240,180,41,0.2); }
.an-insight.teal { background: rgba(0,201,177,0.05); border-color: rgba(0,201,177,0.2); }
.an-insight.coral { background: rgba(255,107,107,0.05); border-color: rgba(255,107,107,0.2); }
.an-insight.lav { background: rgba(167,139,250,0.05); border-color: rgba(167,139,250,0.2); }
`;

// ── Download helper ───────────────────────────────────────────────────────────
function downloadCSV(revenue, products, trends, categories) {
  const lines = ['OptiKa Analytics Report', `Generated: ${new Date().toLocaleString()}`, ''];
  lines.push('=== Revenue Summary ===');
  lines.push('Date,Revenue,Profit');
  (revenue?.days || []).forEach((d) => lines.push(`${d.date},${d.revenue.toFixed(2)},${d.profit.toFixed(2)}`));
  lines.push('');
  lines.push('=== Top Products ===');
  lines.push('Product,Category,Revenue,Units Sold,Margin %');
  (products || []).forEach((p) => lines.push(`"${p.name}","${p.category}",${p.revenue.toFixed(2)},${p.unitsSold},${p.margin}`));
  lines.push('');
  lines.push('=== 6-Month Trend ===');
  lines.push('Month,Revenue,Profit');
  (trends || []).forEach((t) => lines.push(`${t.month},${t.revenue.toFixed(2)},${t.profit.toFixed(2)}`));
  lines.push('');
  lines.push('=== Category Breakdown ===');
  lines.push('Category,Revenue,%');
  (categories?.categories || []).forEach((c) => lines.push(`"${c.category}",${c.revenue.toFixed(2)},${c.pct}`));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `optika-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function Skeleton({ h = 20, w = '100%', r = 6 }) {
  return (
    <div style={{
      height: h, width: w, borderRadius: r, flexShrink: 0,
      backgroundImage: 'linear-gradient(90deg,#1E2235 25%,#252A3D 50%,#1E2235 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.5s infinite',
    }} />
  );
}

// ── Sparkline ─────────────────────────────────────────────────────────────────
function Sparkline({ data, dataKey, color }) {
  if (!data?.length || !dataKey) return null;
  const vals = data.slice(-12).map((d) => Number(d[dataKey] || 0));
  const max = Math.max(...vals, 1);
  const min = Math.min(...vals, 0);
  const range = max - min || 1;
  const pts = vals.map((v, i) => {
    const x = (i / Math.max(vals.length - 1, 1)) * 90;
    const y = 35 - ((v - min) / range) * 30;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg viewBox="0 0 90 40" preserveAspectRatio="none"
      style={{ width: 90, height: 40, opacity: 0.5, position: 'absolute', bottom: 0, right: 0 }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

// ── Category Donut ─────────────────────────────────────────────────────────────
function CategoryDonut({ categories, total }) {
  const C = 2 * Math.PI * 48;
  const cats = (categories || []).slice(0, 5);
  let runningOffset = 0;
  const segments = cats.map((c, i) => {
    const pctVal = parseFloat(c.pct) || 0;
    const dash = (pctVal / 100) * C;
    const seg = { c, i, dash, offset: runningOffset };
    runningOffset += dash;
    return seg;
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
      <div style={{ position: 'relative', width: 120, height: 120, flexShrink: 0 }}>
        <svg width="120" height="120" viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="60" cy="60" r="48" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="16" />
          {segments.map(({ c, i, dash, offset }) => (
            <circle key={c.category}
              cx="60" cy="60" r="48" fill="none"
              stroke={COLORS[i % COLORS.length]}
              strokeWidth="16"
              strokeDasharray={`${dash} ${C - dash}`}
              strokeDashoffset={-offset}
              strokeLinecap="round"
            />
          ))}
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 900, fontFamily: '"JetBrains Mono", monospace', lineHeight: 1, color: '#F0B429' }}>
            {fmtShort(total)}
          </div>
          <div style={{ fontSize: 9, color: '#6B7A99', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 2 }}>Total</div>
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {cats.map((c, i) => (
          <div key={c.category} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: COLORS[i % COLORS.length], flexShrink: 0 }} />
            <div style={{ fontSize: 12, fontWeight: 700, flex: 1, color: '#E2E8F0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.category}</div>
            <div style={{ width: 70, height: 4, borderRadius: 100, background: 'rgba(255,255,255,0.06)', flexShrink: 0 }}>
              <div style={{ height: '100%', width: `${parseFloat(c.pct)}%`, borderRadius: 100, background: COLORS[i % COLORS.length] }} />
            </div>
            <div style={{ fontSize: 11, fontWeight: 800, fontFamily: '"JetBrains Mono", monospace', color: COLORS[i % COLORS.length], width: 46, textAlign: 'right', flexShrink: 0 }}>
              {fmtShort(c.revenue)}
            </div>
            <div style={{ fontSize: 10, color: '#6B7A99', width: 28, textAlign: 'right', flexShrink: 0 }}>{c.pct}%</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Weekly Bar Chart ──────────────────────────────────────────────────────────
function WeeklyBars({ days }) {
  if (!days?.length) return (
    <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7A99', fontSize: 13 }}>
      No data for this period
    </div>
  );

  const bucketCount = days.length <= 7 ? days.length : Math.min(6, Math.ceil(days.length / 7));
  const chunkSize = Math.ceil(days.length / bucketCount);
  const buckets = Array.from({ length: bucketCount }, (_, i) => {
    const chunk = days.slice(i * chunkSize, (i + 1) * chunkSize);
    const rev = chunk.reduce((s, d) => s + (d.revenue || 0), 0);
    const pro = chunk.reduce((s, d) => s + (d.profit || 0), 0);
    const label = days.length <= 7 && days[i * chunkSize]
      ? days[i * chunkSize].date.slice(5)
      : `W${i + 1}`;
    return { label, revenue: rev, profit: pro };
  }).filter((b) => b.revenue > 0 || b.profit > 0);

  if (!buckets.length) return (
    <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7A99', fontSize: 13 }}>
      No revenue recorded
    </div>
  );

  const maxVal = Math.max(...buckets.map((b) => b.revenue), 1);
  const topIdx = buckets.indexOf(buckets.reduce((best, b) => b.revenue > best.revenue ? b : best, buckets[0]));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 180, paddingTop: 20, position: 'relative' }}>
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: 38 }}>
          {[1, 0.75, 0.5, 0.25, 0].map((f, i) => (
            <div key={i} style={{ fontSize: 9, color: '#3D4559', fontFamily: '"JetBrains Mono", monospace', textAlign: 'right', paddingRight: 6 }}>
              {fmtShort(maxVal * f)}
            </div>
          ))}
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 6, paddingLeft: 46, height: '100%' }}>
          {buckets.map((b, i) => {
            const isTop = i === topIdx;
            const revH = `${(b.revenue / maxVal) * 100}%`;
            const proH = `${(Math.max(0, b.profit) / maxVal) * 100}%`;
            return (
              <div key={b.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                <div style={{ flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                  <div
                    title={`Revenue: GHS ${b.revenue.toLocaleString()}`}
                    style={{
                      flex: 1, borderRadius: '4px 4px 0 0', minHeight: 3,
                      height: revH, cursor: 'pointer',
                      background: 'linear-gradient(180deg,#F0B429 0%,rgba(240,180,41,0.2) 100%)',
                      boxShadow: isTop ? '0 0 14px rgba(240,180,41,0.4)' : 'none',
                      transition: 'filter 0.15s',
                    }}
                  />
                  <div
                    title={`Profit: GHS ${b.profit.toLocaleString()}`}
                    style={{
                      flex: 1, borderRadius: '4px 4px 0 0', minHeight: 3,
                      height: proH, cursor: 'pointer',
                      background: 'linear-gradient(180deg,#00C9B1 0%,rgba(0,201,177,0.2) 100%)',
                      transition: 'filter 0.15s',
                    }}
                  />
                </div>
                <div style={{ fontSize: 9, color: isTop ? '#F0B429' : '#3D4559', marginTop: 5, fontFamily: '"JetBrains Mono", monospace' }}>
                  {b.label}{isTop ? ' ★' : ''}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 14 }}>
        {[['Revenue', '#F0B429'], ['Net Profit', '#00C9B1']].map(([label, color]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#6B7A99', fontWeight: 600 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Trend Line ────────────────────────────────────────────────────────────────
function TrendLine({ trends }) {
  if (!trends?.length) return (
    <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7A99', fontSize: 13 }}>
      No trend data yet
    </div>
  );

  const maxVal = Math.max(...trends.map((t) => Math.max(t.revenue || 0, t.profit || 0)), 1);
  const W = 400, H = 130;
  const xStep = W / Math.max(trends.length - 1, 1);
  const sy = (v) => H - ((v || 0) / maxVal) * H;

  const revPts = trends.map((t, i) => `${i * xStep},${sy(t.revenue)}`).join(' ');
  const proPts = trends.map((t, i) => `${i * xStep},${sy(t.profit)}`).join(' ');

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H + 24}`} style={{ width: '100%', height: 160, display: 'block' }}>
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <line key={f} x1="0" y1={H * f} x2={W} y2={H * f} stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
        ))}
        <polyline points={revPts} fill="none" stroke="#F0B429" strokeWidth="2" strokeLinejoin="round" />
        <polyline points={proPts} fill="none" stroke="#00C9B1" strokeWidth="2" strokeLinejoin="round" strokeDasharray="6 3" />
        {trends.map((t, i) => (
          <g key={t.month}>
            <circle cx={i * xStep} cy={sy(t.revenue)} r="3.5" fill="#F0B429" />
            <circle cx={i * xStep} cy={sy(t.profit)} r="3.5" fill="#00C9B1" />
            <text x={i * xStep} y={H + 16} textAnchor="middle" fontSize="10" fill="#6B7A99">{t.month}</text>
          </g>
        ))}
      </svg>
      <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
        {[['Revenue', '#F0B429', false], ['Net Profit', '#00C9B1', true]].map(([label, color, dashed]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#6B7A99', fontWeight: 600 }}>
            <svg width="16" height="6" viewBox="0 0 16 6">
              <line x1="0" y1="3" x2="16" y2="3" stroke={color} strokeWidth="2" strokeDasharray={dashed ? '4 2' : 'none'} />
            </svg>
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Analytics() {
  const [period, setPeriod] = useState('30d');
  const [revenue, setRevenue] = useState(null);
  const [products, setProducts] = useState(null);
  const [trends, setTrends] = useState(null);
  const [categories, setCategories] = useState(null);
  const [sources, setSources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const load = useCallback(async (p) => {
    setLoading(true);
    setFetchError('');
    try {
      const headers = { Authorization: `Bearer ${token()}` };
      const safeFetch = async (url) => {
        const r = await fetch(url, { headers });
        if (!r.ok) {
          const body = await r.json().catch(() => ({ error: r.statusText }));
          throw new Error(`${url.split('/').slice(-1)[0].split('?')[0]}: ${body.error || r.status}`);
        }
        return r.json();
      };
      const [rev, prod, trnd, cat, src] = await Promise.all([
        safeFetch(`${API}/analytics/revenue?period=${p}`),
        safeFetch(`${API}/analytics/top-products?limit=5`),
        safeFetch(`${API}/analytics/trends?months=6`),
        safeFetch(`${API}/analytics/categories`),
        safeFetch(`${API}/analytics/sources?period=${p}`),
      ]);
      setRevenue(rev);
      setProducts(prod);
      setTrends(trnd);
      setCategories(cat);
      setSources(src);
    } catch (e) {
      console.error('[Analytics] fetch error:', e);
      setFetchError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(period); }, [period, load]);

  const insights = useMemo(() => {
    const result = [];
    if (products?.length) {
      const top = products[0];
      result.push({
        color: 'gold', icon: '📦',
        title: `${top.name} drives ${top.pct || 0}% of revenue`,
        text: `Your top product with ${top.margin}% margin. Keeping it always in stock is priority #1.`,
      });
    }
    if (products?.length) {
      const thin = [...products].sort((a, b) => a.margin - b.margin)[0];
      if (thin && thin.margin < 25) {
        result.push({
          color: 'coral', icon: '⚠️',
          title: `${thin.name} margin is thin at ${thin.margin}%`,
          text: 'This is your lowest-margin product. A small price increase could meaningfully boost overall profit.',
        });
      }
    }
    if (categories?.categories?.length) {
      const top = categories.categories[0];
      result.push({
        color: 'teal', icon: '🏆',
        title: `${top.category} leads with ${top.pct}% of sales`,
        text: 'Your strongest category by revenue. Prioritise stock levels and consider promotions here.',
      });
    }
    if (revenue?.avgMargin != null) {
      const m = Number(revenue.avgMargin);
      result.push({
        color: m >= 25 ? 'teal' : 'lav',
        icon: m >= 25 ? '✅' : '💡',
        title: m >= 25 ? `${pct(m)} average margin — healthy` : `${pct(m)} average margin — room to improve`,
        text: m >= 25
          ? 'Your margins are strong. Focus on growing transaction volume and customer retention.'
          : 'Consider reviewing cost prices or adjusting selling prices on lower-margin items.',
      });
    }
    return result;
  }, [products, categories, revenue]);

  const KPI_CONFIGS = [
    { label: 'Total Revenue', value: revenue?.totalRevenue, color: '#F0B429', icon: '💰', dataKey: 'revenue', deltaLabel: 'period' },
    { label: 'Net Profit', value: revenue?.netProfit, color: '#00C9B1', icon: '📈', dataKey: 'profit', deltaLabel: 'after expenses' },
    { label: 'Avg Margin', value: revenue?.avgMargin != null ? `${Number(revenue.avgMargin).toFixed(1)}%` : '—', color: '#FF6B6B', icon: '🎯', isText: true, deltaLabel: 'gross margin' },
    { label: 'Gross Profit', value: revenue?.grossProfit, color: '#A78BFA', icon: '🏦', dataKey: 'profit', deltaLabel: 'revenue - COGS' },
  ];

  return (
    <div className="an-wrap" style={{ padding: '28px 32px', background: '#0B0E17', minHeight: '100%', position: 'relative', overflow: 'hidden' }}>
      <style>{STYLES}</style>

      {/* Gradient orbs */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: [
          'radial-gradient(ellipse 700px 600px at 0% 0%,rgba(27,58,107,0.6) 0%,transparent 65%)',
          'radial-gradient(ellipse 500px 400px at 100% 60%,rgba(240,180,41,0.08) 0%,transparent 65%)',
          'radial-gradient(ellipse 400px 400px at 40% 100%,rgba(0,201,177,0.06) 0%,transparent 65%)',
        ].join(','),
      }} />
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.012) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.012) 1px,transparent 1px)',
        backgroundSize: '44px 44px',
      }} />

      <div style={{ position: 'relative', zIndex: 1 }}>

        {/* Error banner */}
        {fetchError && (
          <div style={{ background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.25)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#FF6B6B', fontWeight: 600 }}>⚠ {fetchError}</span>
            <button onClick={() => load(period)} style={{ background: '#FF6B6B', color: '#0B0E17', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>
              Retry
            </button>
          </div>
        )}

        {/* Topbar */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 900, letterSpacing: -1, lineHeight: 1.1, color: '#FFFFFF' }}>
              Business{' '}
              <em style={{ fontFamily: '"Instrument Serif", serif', fontStyle: 'italic', color: '#F0B429' }}>Analytics</em>
            </h1>
            <div style={{ fontSize: 13, color: '#6B7A99', marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>Revenue, profit &amp; performance</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, background: 'rgba(0,201,177,0.12)', color: '#00C9B1', padding: '3px 9px', borderRadius: 100, border: '1px solid rgba(0,201,177,0.2)' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#00C9B1', animation: 'an-blink 1.5s infinite', display: 'inline-block' }} />
                Live
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: 3, gap: 2 }}>
              {PERIODS.map((p) => (
                <button key={p} className={'an-sw' + (period === p ? ' on' : '')} onClick={() => setPeriod(p)}>
                  {PERIOD_LABELS[p]}
                </button>
              ))}
            </div>
            <button
              onClick={() => downloadCSV(revenue, products, trends, categories)}
              disabled={loading}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, padding: '0 18px', height: 38, borderRadius: 10,
                background: '#F0B429', color: '#0B0E17', fontSize: 13, fontWeight: 800,
                cursor: loading ? 'default' : 'pointer', border: 'none', fontFamily: 'inherit',
                opacity: loading ? 0.5 : 1, transition: 'all 0.15s',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Export Report
            </button>
          </div>
        </div>

        {/* KPI Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 20 }}>
          {loading ? Array(4).fill(0).map((_, i) => (
            <div key={i} className="an-kpi">
              <Skeleton h={10} w="60%" r={4} />
              <div style={{ height: 12 }} />
              <Skeleton h={28} w="75%" r={6} />
              <div style={{ height: 8 }} />
              <Skeleton h={20} w="45%" r={20} />
            </div>
          )) : KPI_CONFIGS.map((k) => (
            <div key={k.label} className="an-kpi" style={{ borderTop: '3px solid ' + k.color }}>
              <div style={{ position: 'absolute', top: 14, right: 14, fontSize: 20, opacity: 0.18 }}>{k.icon}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7A99', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 8 }}>
                {k.label}
              </div>
              <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: -1, fontFamily: '"JetBrains Mono", monospace', lineHeight: 1, color: k.color }}>
                {k.isText ? k.value : fmtShort(k.value)}
              </div>
              <div style={{ marginTop: 8 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 100, background: k.color + '18', color: k.color }}>
                  {k.deltaLabel}
                </span>
              </div>
              <Sparkline data={revenue?.days} dataKey={k.dataKey} color={k.color} />
            </div>
          ))}
        </div>

        {/* Row 1: Bar chart + Category donut */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
          <div className="an-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: -0.3, color: '#FFFFFF' }}>Revenue · Profit</div>
                <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 2 }}>
                  {period === '7d' ? 'Daily' : 'Weekly'} breakdown · {PERIOD_LABELS[period]}
                </div>
              </div>
              {!loading && revenue && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <span className="an-chip gold">GHS {fmtShort(revenue.totalRevenue)} Rev</span>
                  <span className="an-chip teal">GHS {fmtShort(revenue.netProfit)} Profit</span>
                </div>
              )}
            </div>
            {loading ? <Skeleton h={200} /> : <WeeklyBars days={revenue?.days} />}
          </div>

          <div className="an-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: -0.3, color: '#FFFFFF' }}>Revenue by Category</div>
                <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 2 }}>This period</div>
              </div>
            </div>
            {loading ? (
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <Skeleton h={120} w={120} r={60} />
                <div style={{ flex: 1 }}>
                  {Array(5).fill(0).map((_, i) => (
                    <div key={i} style={{ marginBottom: 10 }}><Skeleton h={11} /></div>
                  ))}
                </div>
              </div>
            ) : !categories?.categories?.length ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#6B7A99', fontSize: 13 }}>No category data yet</div>
            ) : (
              <CategoryDonut categories={categories.categories} total={categories.total} />
            )}
          </div>
        </div>

        {/* Row 2: Top Products + 6-Month Trend */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>

          {/* Top Products */}
          <div className="an-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: -0.3, color: '#FFFFFF' }}>Top Products</div>
                <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 2 }}>By revenue this period</div>
              </div>
              <span className="an-chip gold">Revenue</span>
            </div>
            {loading ? Array(5).fill(0).map((_, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <Skeleton h={36} w={36} r={9} />
                <div style={{ flex: 1 }}>
                  <Skeleton h={12} w="70%" r={4} />
                  <div style={{ height: 5 }} />
                  <Skeleton h={9} w="45%" r={4} />
                </div>
              </div>
            )) : !products?.length ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#6B7A99', fontSize: 13 }}>No sales data yet</div>
            ) : products.map((p, i) => {
              const isTop = i < 2;
              const mClass = p.margin >= 30 ? 'teal' : p.margin >= 20 ? 'gold' : 'coral';
              return (
                <div key={p.id} className="an-prod-row">
                  <div style={{ fontSize: 11, fontWeight: 800, color: isTop ? '#F0B429' : '#3D4559', width: 18, textAlign: 'center', flexShrink: 0 }}>{i + 1}</div>
                  <div style={{ width: 36, height: 36, borderRadius: 9, background: COLORS[i % COLORS.length] + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                    📦
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 1 }}>{p.unitsSold} units · {p.margin}% margin</div>
                  </div>
                  <span className={'an-chip ' + mClass} style={{ fontFamily: '"JetBrains Mono", monospace', flexShrink: 0 }}>{p.margin}%</span>
                  <div style={{ fontSize: 13, fontWeight: 800, fontFamily: '"JetBrains Mono", monospace', color: '#F0B429', flexShrink: 0, marginLeft: 6 }}>
                    {fmtShort(p.revenue)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* 6-Month Trend */}
          <div className="an-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: -0.3, color: '#FFFFFF' }}>6-Month Trend</div>
                <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 2 }}>Revenue vs profit over time</div>
              </div>
              <span className="an-chip teal">Trend</span>
            </div>
            {loading ? <Skeleton h={180} /> : <TrendLine trends={trends} />}
          </div>
        </div>

        {/* Row 3: Source of Business */}
        <div className="an-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: -0.3, color: '#FFFFFF' }}>Source of Business</div>
              <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 2 }}>Where your customers are coming from</div>
            </div>
            {!loading && sources && <span className="an-chip teal">{sources.total} sales tracked</span>}
          </div>

          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array(5).fill(0).map((_, i) => <Skeleton key={i} h={36} r={8} />)}
            </div>
          ) : !sources?.sources?.length ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#6B7A99', fontSize: 13 }}>
              No source data yet — select a source at checkout to start tracking
            </div>
          ) : (() => {
            const SOURCE_META = {
              walk_in:       { label: 'Walk-in',       icon: '🚶' },
              returning:     { label: 'Returning',     icon: '🔄' },
              whatsapp:      { label: 'WhatsApp',      icon: '💬' },
              instagram:     { label: 'Instagram',     icon: '📸' },
              tiktok:        { label: 'TikTok',        icon: '🎵' },
              facebook:      { label: 'Facebook',      icon: '👍' },
              jiji:          { label: 'Jiji',          icon: '🛒' },
              google:        { label: 'Google',        icon: '🔍' },
              friend_family: { label: 'Friend/Family', icon: '👥' },
              flyer:         { label: 'Flyer/Poster',  icon: '📄' },
              other:         { label: 'Other',         icon: '✨' },
              unknown:       { label: 'Not recorded',  icon: '❓' },
            };
            const maxRev = Math.max(...sources.sources.map(s => s.revenue), 1);
            const barColors = ['#00C9B1', '#F0B429', '#A78BFA', '#38BDF8', '#FF6B6B', '#FB7185'];
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {sources.sources.map((s, i) => {
                  const meta = SOURCE_META[s.source] || { label: s.source, icon: '📌' };
                  const color = barColors[i % barColors.length];
                  const pct = Math.round((s.revenue / maxRev) * 100);
                  return (
                    <div key={s.source} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ fontSize: 16, width: 24, textAlign: 'center', flexShrink: 0 }}>{meta.icon}</div>
                      <div style={{ width: 110, fontSize: 12, fontWeight: 700, color: '#FFFFFF', flexShrink: 0 }}>{meta.label}</div>
                      <div style={{ flex: 1, height: 8, borderRadius: 100, background: 'rgba(255,255,255,0.06)' }}>
                        <div style={{ height: '100%', width: `${pct}%`, borderRadius: 100, background: color, transition: 'width 0.5s ease' }} />
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7A99', width: 28, textAlign: 'right', flexShrink: 0 }}>{s.count}</div>
                      <div style={{ fontSize: 12, fontWeight: 800, fontFamily: '"JetBrains Mono", monospace', color, width: 80, textAlign: 'right', flexShrink: 0 }}>
                        {s.revenue >= 1000 ? `GHS ${(s.revenue / 1000).toFixed(1)}k` : `GHS ${s.revenue.toFixed(0)}`}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* Row 4: AI Insights */}
        <div className="an-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: -0.3, color: '#FFFFFF' }}>AI Insights</div>
              <div style={{ fontSize: 11, color: '#6B7A99', marginTop: 2 }}>Powered by OptiKa AI · derived from your data</div>
            </div>
            {!loading && <span className="an-chip lav">{insights.length} insight{insights.length !== 1 ? 's' : ''}</span>}
          </div>
          {loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {Array(4).fill(0).map((_, i) => <Skeleton key={i} h={80} r={12} />)}
            </div>
          ) : !insights.length ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#6B7A99', fontSize: 13 }}>
              Add sales data to generate insights
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
              {insights.map((ins, i) => (
                <div key={i} className={'an-insight ' + ins.color}>
                  <div style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>{ins.icon}</div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 3, color: '#FFFFFF' }}>{ins.title}</div>
                    <div style={{ fontSize: 12, color: '#6B7A99', lineHeight: 1.5 }}>{ins.text}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
